import { z } from 'zod';
export const ANALYTICS_EVENT_TYPES = ['SCREEN_VIEW', 'CLICK'];
/** One tracked event from the client. Coordinates are viewport pixels. */
export const analyticsEventInputSchema = z.object({
    type: z.enum(ANALYTICS_EVENT_TYPES),
    name: z.string().trim().min(1).max(120),
    path: z.string().trim().max(200).optional(),
    x: z.number().int().min(0).max(100000).optional(),
    y: z.number().int().min(0).max(100000).optional(),
    vw: z.number().int().min(0).max(100000).optional(),
    vh: z.number().int().min(0).max(100000).optional(),
});
export const trackEventsSchema = z.object({
    events: z.array(analyticsEventInputSchema).min(1).max(50),
});
/* ---------- admin dashboard responses ---------- */
export const funnelStepSchema = z.object({
    name: z.string(),
    label: z.string(),
    count: z.number().int(),
});
export const namedCountSchema = z.object({ name: z.string(), count: z.number().int() });
export const timeseriesPointSchema = z.object({
    date: z.string(),
    events: z.number().int(),
    users: z.number().int(),
});
export const analyticsOverviewSchema = z.object({
    rangeDays: z.number().int(),
    totals: z.object({
        events: z.number().int(),
        screenViews: z.number().int(),
        clicks: z.number().int(),
        users: z.number().int(),
    }),
    funnel: z.array(funnelStepSchema),
    topClicks: z.array(namedCountSchema),
    topScreens: z.array(namedCountSchema),
    timeseries: z.array(timeseriesPointSchema).default([]),
});
export const clickPointSchema = z.object({
    x: z.number(),
    y: z.number(),
    vw: z.number(),
    vh: z.number(),
});
/* ---------- CRM-style BI dashboard ---------- */
export const dayCountSchema = z.object({ date: z.string(), count: z.number().int() });
export const dayUsersSchema = z.object({ date: z.string(), users: z.number().int() });
export const biTopVisitorSchema = z.object({
    userId: z.string(),
    name: z.string(),
    username: z.string().nullable(),
    bookings: z.number().int(),
    confirmed: z.number().int(),
    lastBookingAt: z.string().nullable(),
});
export const biOverviewSchema = z.object({
    rangeDays: z.number().int(),
    /** Active users over fixed rolling windows + DAU/MAU stickiness (%). */
    activeUsers: z.object({
        dau: z.number().int(),
        wau: z.number().int(),
        mau: z.number().int(),
        stickiness: z.number().int(),
    }),
    /** New users (joined) within the selected range. */
    newUsers: z.number().int(),
    /** Distinct active users per day across the range. */
    dauTrend: z.array(dayUsersSchema),
    /** Activity matrix, Moscow time: 7 rows (Mon..Sun) × 24 hour columns. */
    hourly: z.array(z.array(z.number().int())),
    bookings: z.object({
        total: z.number().int(),
        confirmed: z.number().int(),
        cancelled: z.number().int(),
        pending: z.number().int(),
        confirmationRate: z.number().int(),
        trend: z.array(dayCountSchema),
    }),
    /** Lifetime top visitors by booking count (with confirmed subcount). */
    topVisitors: z.array(biTopVisitorSchema),
});
export const clickMapSchema = z.object({
    path: z.string().nullable(),
    points: z.array(clickPointSchema),
    /** Top clicked elements (by `data-track` name) for this path — the actual
     *  insight when raw coordinates are noisy across viewports. */
    elements: z.array(namedCountSchema).default([]),
});
